import Vue from 'vue'
import Vuex from 'vuex'
import httpClient from '@/plugins/axios'
import renew from './renew.js'
import motor from './motor.js'
import ramadan from './ramadan.js'
import insurance from './insurance.js'
import i18n from '@/i18n'
import {
  MONTH_PAYMENT_EXPERIMENT,
  FULL_PAYMENT_EXPERIMENT,
  AB_TESTING_GROUPS, RENEW_UPGRADE_EXPERIMENT_092023
} from '../constants/experimentNames.js'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    abDistribution: null,
    typeOfPayment: null
  },
  getters: {
    savedLocale () {
      return localStorage.getItem('savedLocale') || null
    },
    carPlateTypeList () {
      return [
        {
          id: 'PRV',
          title: i18n.t('car_plate_type_vehicle')
        },
        {
          id: 'PRT',
          title: i18n.t('car_plate_type_transportation')
        }
      ]
    }
  },
  mutations: {
    saveLocale (locale) {
      localStorage.setItem('savedLocale', locale)
    },
    setABDistribution (state, experiment) {
      state.abDistribution = experiment
    },
    setTypeOfPayment (state, testingGroup) {
      const distribution = {
        [AB_TESTING_GROUPS.A1]: MONTH_PAYMENT_EXPERIMENT,
        [AB_TESTING_GROUPS.A2]: MONTH_PAYMENT_EXPERIMENT,
        [AB_TESTING_GROUPS.B1]: FULL_PAYMENT_EXPERIMENT,
        [AB_TESTING_GROUPS.B2]: FULL_PAYMENT_EXPERIMENT
      }
      state.typeOfPayment = distribution[testingGroup]
    }
  },
  actions: {
    setAdditionalCoverPrice (_, params) {
      return httpClient.get('updateassuredsum', { params })
        .then(({ data }) => data)
    },
    getActivePolicies (_, params) {
      return httpClient.get('/getactivepolicies', { params: { ...params, includeall: 1 } })
        .then(({ data }) => {
          if (process.env.VUE_APP_IS_DEV) {
            localStorage.setItem('car-insurance_renew-login', JSON.stringify({ policy: data.policies[0] }))
          }
          return data.policies
        })
    },
    sendFormData (_, data) {
      const formData = Object.entries(data).reduce((d, [key, value]) => {
        return `${d ? d + '&' : ''}fields[${key}]=${value}`
      }, '')

      return httpClient.post('sendformdata', formData)
    },
    getFormData (_, fields) {
      return httpClient.get('getformdata', {
        params: {
          fields
        }
      })
        .then(({ data }) => data.fields)
    },
    getTplPrice () {
      return httpClient.get('gettplprice')
        .then(({ data }) => ({
          quote: data.quoteno,
          schemes: data.schemes
        }))
    },
    getTplQuote () {
      return this.dispatch('getTplPrice')
        .then(({ quote, schemes }) => ({
          quoteno: quote,
          price: Number(schemes[0]?.price) || 0,
          schemecode: schemes[0]?.schemecode || 0,
          productcode: schemes[0]?.productcode || 0,
          discount: Number(schemes[0]?.discount) || 0,
          included: schemes[0]?.included.map(({ name, description, value }) => ({
            name,
            description,
            value: parseInt(value)
          })),
          ids: schemes[0]?.ids.map(({ name, description, value }) => ({
            name,
            description,
            value: parseInt(value)
          }))
        }))
    },
    getTotalPrice (_, data) {
      const formData = Object.entries(data).reduce((d, [key, value]) => {
        if (Array.isArray(value)) {
          return value.reduce((prev, item) => {
            return `${prev ? prev + '&' : ''}${key}[]=${item}`
          }, d)
        }
        return `${d ? d + '&' : ''}${key}=${value}`
      }, '')

      return httpClient.post('gettotalprice', formData)
        .then(({ data }) => Number(data.totalprice))
    },
    getPaymentLink ({ dispatch }, data) {
      return dispatch('getTotalPrice', {
        quoteno: data.quoteno,
        schemecode: data.schemecode,
        productcode: data.productcode,
        selectproduct: true
      })
        .then(() => data.paymentMethod === 0 ? process.env.VUE_APP_REDIRECT_CC : process.env.VUE_APP_REDIRECT_DC)
    },
    getPolicy () {
      return httpClient.get('getpolicy')
        .then(({ data }) => data)
    },
    sendFeedback (_, data) {
      const formData = Object.entries(data).reduce((d, [key, value]) => {
        return `${d ? d + '&' : ''}${key}=${value}`
      }, '')

      return httpClient.post('sendfeedback', formData)
        .then(() => true)
    },
    sendInsuranceRate (_, data) {
      return this.dispatch('sendFeedback', {
        comment: data.rateMessage,
        rating: data.rate
      })
    },
    sendCallback (_, data) {
      const formData = Object.entries(data).reduce((d, [key, value]) => {
        return `${d ? d + '&' : ''}${key}=${value}`
      }, '')

      return httpClient.post('sendcallback', formData)
        .then(() => true)
    },
    getPromoDiscount (_, data) {
      const formData = Object.entries(data).reduce((d, [key, value]) => {
        return `${d ? d + '&' : ''}${key}=${value}`
      }, '')

      return httpClient.post('getpromodiscount', formData)
        .then(({ data }) => data.discount)
    },
    updateMotorInfo (_, quoteNumber) {
      return httpClient.post('/policyrenewalupdatemotor', `quoteno=${quoteNumber}`)
    },
    getCarValueXA ({ state }, chasiss) {
      return httpClient.get('/xa/allinonev1', { params: { vin: chasiss, isnew: 0 } })
        .then((resp) => {
          const { VehicleValues, MinCarValue } = resp.data?.data ?? {}
          const values = {
            vehicleValues: null,
            minCarValue: null
          }

          if (VehicleValues) {
            values.vehicleValues = {
              medium: VehicleValues?.Medium,
              low: VehicleValues?.Low,
              high: VehicleValues?.High,
              currency: VehicleValues?.CurrencyCode
            }
          }

          if (MinCarValue) values.minCarValue = MinCarValue

          return values
        })
    },
    validatePromocode (_, promocode) {
      return httpClient.post(`validatepromocode?promocode=${promocode}`)
        .then(({ data }) => data)
    },
    getABDistribution () {
      return httpClient.get('getabdistribution')
        .then(({ data }) => {
          const experiment = data.list.find((listItem) =>
            listItem.experiment === RENEW_UPGRADE_EXPERIMENT_092023
          )
          const experimentData = experiment?.group || AB_TESTING_GROUPS.A1
          this.commit('setABDistribution', experimentData)
          this.commit('setTypeOfPayment', experimentData)
        })
        .catch(() => this.commit('setABDistribution', null))
    },
    getAffinityData (_, promoCode) {
      return httpClient.get(`getaffinitydata?code=${promoCode}`)
        .then(({ data }) => data)
    },
    getBoatRegistryPorts () {
      return httpClient.get('/getlists/registry_ports')
        .then(({ data }) => data.list)
    },
    getBoatEngines () {
      return httpClient.get('/getlists/vessel_engines')
        .then(({ data }) => data.list)
    }
  },
  modules: {
    renew,
    insurance,
    motor,
    ramadan
  }
})
